import { useState, FormEvent, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const FORM_URL = 'https://readdy.ai/api/form/d6m7vf2qoe30lj0v88sg';
const ZAPIER_WEBHOOK_URL = '';

const SERVICES = [
  { value: 'Alarm System', icon: 'ri-alarm-warning-line' },
  { value: 'Security Cameras', icon: 'ri-camera-line' },
  { value: 'Access Control', icon: 'ri-door-lock-line' },
  { value: '24/7 Monitoring', icon: 'ri-eye-line' },
  { value: 'Smart Home', icon: 'ri-home-gear-line' },
  { value: 'Video Doorbell', icon: 'ri-door-open-line' },
];

const TRUST_BADGES = [
  { icon: 'ri-shield-check-line', label: 'Licensed & Insured' },
  { icon: 'ri-time-line', label: '2-Hour Response' },
  { icon: 'ri-star-fill', label: '4.9★ Google Rating' },
  { icon: 'ri-price-tag-3-line', label: 'No Hidden Fees' },
  { icon: 'ri-award-line', label: 'BBB A+ Rated' },
  { icon: 'ri-tools-line', label: '30,000+ Installs' },
];

const TESTIMONIALS = [
  {
    name: 'Michael T.',
    city: 'Toronto',
    text: 'Got a call back within 45 minutes. The technician was professional and the system has been flawless for 2 years.',
    stars: 5,
    service: 'Alarm + Cameras',
    img: 'https://public.readdy.ai/ai/img_res/f52414641b650de9278a0bd6e12d49e3.jpg',
  },
  {
    name: 'Sarah K.',
    city: 'Mississauga',
    text: 'The free quote was detailed and honest — no upselling. They installed exactly what I needed at a fair price.',
    stars: 5,
    service: 'Home Security',
    img: 'https://readdy.ai/api/search-image?query=professional%20woman%20smiling%20confident%20portrait%20clean%20white%20background%20natural%20lighting%20headshot%20brunette&width=200&height=200&seq=qt2&orientation=squarish',
  },
  {
    name: 'David R.',
    city: 'Brampton',
    text: 'Best security company in the GTA. Compared 4 quotes — Alliance was the most transparent and professional.',
    stars: 5,
    service: 'Commercial Security',
    img: 'https://readdy.ai/api/search-image?query=professional%20south%20asian%20man%20smiling%20confident%20portrait%20clean%20white%20background%20natural%20lighting%20headshot%20business&width=200&height=200&seq=qt3&orientation=squarish',
  },
];

const FAQS = [
  {
    q: 'How quickly will I receive my quote?',
    a: 'Our security specialists respond within 2 business hours. For urgent requests, call us directly at 1-888-458-9181.',
  },
  {
    q: 'Is the quote really free with no obligation?',
    a: 'Absolutely. There is zero cost and zero pressure. We provide a detailed, itemized quote and you decide if you want to proceed.',
  },
  {
    q: 'Do you service my area?',
    a: 'We cover the entire Greater Toronto Area including Toronto, Mississauga, Brampton, Oakville, Hamilton, Richmond Hill, Vaughan, Whitby, Ajax, and Oshawa.',
  },
  {
    q: 'What happens after I submit the form?',
    a: 'A certified security specialist reviews your details, prepares a custom quote, and contacts you by phone or email within 2 hours.',
  },
];

function useCountUp(target: number, duration: number, start: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

export default function GetQuotePage() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    propertyType: '',
    city: '',
    services: [] as string[],
    details: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [detailsLen, setDetailsLen] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const installs = useCountUp(30000, 2000, statsVisible);
  const years = useCountUp(25, 1500, statsVisible);
  const satisfaction = useCountUp(98, 1800, statsVisible);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const toggleService = (val: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(val)
        ? prev.services.filter(s => s !== val)
        : [...prev.services, val],
    }));
  };

  const isValid =
    formData.fullName.trim() !== '' &&
    formData.email.trim() !== '' &&
    formData.phone.trim() !== '' &&
    formData.propertyType !== '' &&
    formData.city !== '' &&
    formData.services.length > 0;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isValid || formData.details.length > 500) return;
    setStatus('submitting');

    const params = new URLSearchParams();
    params.append('fullName', formData.fullName);
    params.append('email', formData.email);
    params.append('phone', formData.phone);
    params.append('propertyType', formData.propertyType);
    params.append('city', formData.city);
    params.append('services', formData.services.join(', '));
    params.append('details', formData.details);

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        if (ZAPIER_WEBHOOK_URL) {
          try {
            await fetch(ZAPIER_WEBHOOK_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...formData, services: formData.services.join(', '), source: 'Get Quote Landing Page', submitted_at: new Date().toISOString() }),
            });
          } catch { /* non-blocking */ }
        }
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <SEO
        title="Free Security System Quote GTA | Alliance Security Systems"
        description="Get a free, no-obligation security quote in minutes. Alarm systems, security cameras, access control & 24/7 monitoring across Toronto, Mississauga, Brampton & the GTA. Licensed experts. 2-hour response."
        keywords="free security quote GTA, alarm system quote Toronto, security camera quote Mississauga, home security quote Ontario"
        canonical="/get-quote"
      />

      {/* Sticky Top Bar */}
      <div className="bg-[#0A1628] text-white py-2 px-4 text-center text-xs font-medium">
        <span className="text-[#D4A853] font-bold">LIMITED TIME:</span> Free professional security assessment with every quote — 
        <a href="tel:18884589181" className="underline ml-1 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap">Call 1-888-458-9181</a>
      </div>

      {/* Navbar */}
      <nav className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex flex-col leading-none border-2 border-[#D4A853] rounded-lg px-3 py-2 bg-[#0A1628]">
              <span className="text-xl font-extrabold tracking-wider text-[#D4A853]">ALLIANCE</span>
              <span className="text-[9px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/alarm-packages" className="text-sm text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer">Alarms</Link>
            <Link to="/camera-packages" className="text-sm text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer">Cameras</Link>
            <Link to="/access-control" className="text-sm text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer">Access Control</Link>
            <Link to="/live-monitoring" className="text-sm text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer">Monitoring</Link>
            <a href="tel:18884589181" className="flex items-center gap-2 text-sm font-semibold text-[#0A1628] hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap">
              <div className="w-7 h-7 bg-[#D4A853] rounded-full flex items-center justify-center">
                <i className="ri-phone-fill text-white text-xs"></i>
              </div>
              1-888-458-9181
            </a>
          </div>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-9 h-9 flex items-center justify-center text-gray-700 cursor-pointer">
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-4 space-y-1">
            <Link to="/alarm-packages" className="block py-2.5 text-sm text-gray-700 border-b border-gray-50">Alarms</Link>
            <Link to="/camera-packages" className="block py-2.5 text-sm text-gray-700 border-b border-gray-50">Cameras</Link>
            <Link to="/access-control" className="block py-2.5 text-sm text-gray-700 border-b border-gray-50">Access Control</Link>
            <Link to="/live-monitoring" className="block py-2.5 text-sm text-gray-700 border-b border-gray-50">Monitoring</Link>
            <a href="tel:18884589181" className="flex items-center gap-2 py-2.5 text-sm font-semibold text-[#D4A853]"><i className="ri-phone-line"></i>1-888-458-9181</a>
          </div>
        )}
      </nav>

      <main>
        {/* ── HERO + FORM ── */}
        <section className="relative min-h-[92vh] flex items-stretch overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20luxury%20home%20exterior%20at%20golden%20hour%20warm%20sunlight%20professional%20residential%20architecture%20clean%20lines%20large%20windows%20manicured%20lawn%20upscale%20neighborhood%20Ontario%20Canada%20suburban&width=1920&height=1080&seq=gq-hero-bg&orientation=landscape"
              alt="Secure home"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/92 via-[#0A1628]/75 to-[#0A1628]/30"></div>
          </div>

          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 py-14 flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            {/* Left — Headline */}
            <div className="flex-1 text-white">
              <div className="inline-flex items-center gap-2 bg-[#D4A853]/20 border border-[#D4A853]/40 text-[#D4A853] px-4 py-2 rounded-full text-xs font-bold tracking-widest mb-6 whitespace-nowrap">
                <i className="ri-shield-check-line"></i>
                100% FREE · NO OBLIGATION
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-5">
                Get Your Free<br />
                <span className="text-[#D4A853]">Security Quote</span><br />
                in Minutes
              </h1>
              <p className="text-lg text-white/80 mb-8 max-w-lg leading-relaxed">
                Tell us about your property and a certified security specialist will build a custom solution — cameras, alarms, access control — tailored to your needs and budget.
              </p>

              {/* Trust badges */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
                {TRUST_BADGES.map((b, i) => (
                  <div key={i} className="flex items-center gap-2.5 bg-white/8 backdrop-blur-sm border border-white/15 rounded-xl px-3 py-2.5">
                    <div className="w-7 h-7 flex items-center justify-center shrink-0">
                      <i className={`${b.icon} text-[#D4A853] text-base`}></i>
                    </div>
                    <span className="text-xs font-semibold text-white/90 leading-tight">{b.label}</span>
                  </div>
                ))}
              </div>

              {/* Social proof strip */}
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map(n => (
                    <img
                      key={n}
                      src={`https://readdy.ai/api/search-image?query=professional%20person%20smiling%20headshot%20portrait%20clean%20background%20natural%20lighting%20diverse&width=80&height=80&seq=sp${n}&orientation=squarish`}
                      alt="Customer"
                      className="w-9 h-9 rounded-full border-2 border-white object-cover object-top"
                    />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-0.5 mb-0.5">
                    {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-[#D4A853] text-xs"></i>)}
                  </div>
                  <p className="text-xs text-white/70">Trusted by <strong className="text-white">30,000+</strong> GTA homeowners</p>
                </div>
              </div>
            </div>

            {/* Right — Form */}
            <div className="w-full lg:w-[480px] shrink-0">
              {status === 'success' ? (
                <SuccessCard />
              ) : (
                <form
                  id="get-quote-landing-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="bg-white rounded-3xl shadow-2xl overflow-hidden"
                >
                  {/* Form header */}
                  <div className="bg-[#0A1628] px-7 py-5">
                    <h2 className="text-white font-bold text-xl">Get My Free Quote</h2>
                    <p className="text-white/60 text-xs mt-1">We respond within 2 hours · No spam, ever</p>
                  </div>

                  <div className="px-7 py-6 space-y-5">
                    {/* Name + Phone */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Full Name *</label>
                        <input
                          type="text"
                          name="fullName"
                          required
                          value={formData.fullName}
                          onChange={e => setFormData(p => ({ ...p, fullName: e.target.value }))}
                          placeholder="John Smith"
                          className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Phone *</label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                          placeholder="(416) 555-0123"
                          className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                        placeholder="john@example.com"
                        className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent"
                      />
                    </div>

                    {/* Property + City */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Property Type *</label>
                        <select
                          name="propertyType"
                          required
                          value={formData.propertyType}
                          onChange={e => setFormData(p => ({ ...p, propertyType: e.target.value }))}
                          className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent cursor-pointer bg-white"
                        >
                          <option value="">Select...</option>
                          <option value="Detached Home">Detached Home</option>
                          <option value="Condo / Apartment">Condo / Apt</option>
                          <option value="Townhouse">Townhouse</option>
                          <option value="Small Business">Small Business</option>
                          <option value="Office / Commercial">Office / Commercial</option>
                          <option value="Warehouse / Industrial">Warehouse</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Your City *</label>
                        <select
                          name="city"
                          required
                          value={formData.city}
                          onChange={e => setFormData(p => ({ ...p, city: e.target.value }))}
                          className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent cursor-pointer bg-white"
                        >
                          <option value="">Select...</option>
                          {['Toronto','Mississauga','Brampton','Oakville','Hamilton','Richmond Hill','Vaughan','Whitby','Ajax','Oshawa','Other'].map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Services */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2">Services Needed * <span className="text-gray-400 font-normal">(select all that apply)</span></label>
                      <div className="grid grid-cols-3 gap-2">
                        {SERVICES.map(svc => (
                          <button
                            key={svc.value}
                            type="button"
                            onClick={() => toggleService(svc.value)}
                            className={`flex flex-col items-center gap-1.5 py-2.5 px-2 rounded-xl border-2 text-center transition-all cursor-pointer ${
                              formData.services.includes(svc.value)
                                ? 'border-[#D4A853] bg-[#D4A853]/8 text-[#D4A853]'
                                : 'border-gray-200 text-gray-500 hover:border-[#D4A853]/40'
                            }`}
                          >
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className={`${svc.icon} text-base`}></i>
                            </div>
                            <span className="text-[10px] font-semibold leading-tight">{svc.value}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Details */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Additional Details <span className="text-gray-400 font-normal">(optional)</span></label>
                      <textarea
                        name="details"
                        rows={2}
                        maxLength={500}
                        value={formData.details}
                        onChange={e => { setFormData(p => ({ ...p, details: e.target.value })); setDetailsLen(e.target.value.length); }}
                        placeholder="e.g. 3-bedroom home, previous break-in, need cameras at back..."
                        className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent resize-none"
                      />
                      <p className="text-[10px] text-gray-400 text-right mt-0.5">{detailsLen}/500</p>
                    </div>

                    {status === 'error' && (
                      <div className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-3 rounded-xl text-xs">
                        <i className="ri-error-warning-line text-base"></i>
                        Something went wrong. Please try again or call 1-888-458-9181.
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={!isValid || status === 'submitting'}
                      className="w-full bg-[#D4A853] text-white py-4 rounded-2xl font-bold text-base hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#D4A853]/25 flex items-center justify-center gap-2"
                    >
                      {status === 'submitting' ? (
                        <><i className="ri-loader-4-line animate-spin text-lg"></i>Sending...</>
                      ) : (
                        <><i className="ri-send-plane-fill text-lg"></i>Get My Free Quote Now</>
                      )}
                    </button>

                    <p className="text-center text-[10px] text-gray-400 leading-relaxed">
                      <i className="ri-lock-2-line mr-1"></i>
                      Your information is 100% private and never shared. By submitting you agree to be contacted by our team.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* ── ANIMATED STATS ── */}
        <section ref={statsRef} className="bg-[#D4A853] py-10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-3 gap-6 text-center">
              {[
                { value: installs, suffix: '+', label: 'Installations Completed' },
                { value: years, suffix: ' yrs', label: 'Years of Experience' },
                { value: satisfaction, suffix: '%', label: 'Customer Satisfaction' },
              ].map((s, i) => (
                <div key={i}>
                  <p className="text-4xl sm:text-5xl font-extrabold text-white">{s.value.toLocaleString()}{s.suffix}</p>
                  <p className="text-white/80 text-sm mt-1 font-medium">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
            <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-xs font-bold tracking-widest mb-4 whitespace-nowrap">HOW IT WORKS</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A1628] mb-12">Your Quote in 3 Simple Steps</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 relative">
              {/* Connector line */}
              <div className="hidden sm:block absolute top-10 left-[calc(16.67%+1rem)] right-[calc(16.67%+1rem)] h-0.5 bg-[#D4A853]/30"></div>
              {[
                { step: 1, icon: 'ri-file-list-3-line', title: 'Fill the Form', desc: 'Tell us about your property and security needs. Takes under 2 minutes.' },
                { step: 2, icon: 'ri-customer-service-2-line', title: 'Expert Review', desc: 'A certified specialist reviews your details and prepares a custom quote.' },
                { step: 3, icon: 'ri-shield-check-line', title: 'Get Protected', desc: 'Receive your detailed quote within 2 hours and schedule your installation.' },
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center relative">
                  <div className="w-20 h-20 bg-[#0A1628] rounded-2xl flex items-center justify-center mb-5 relative z-10 shadow-xl">
                    <i className={`${item.icon} text-3xl text-[#D4A853]`}></i>
                    <div className="absolute -top-2 -right-2 w-7 h-7 bg-[#D4A853] rounded-full flex items-center justify-center text-white text-xs font-extrabold">{item.step}</div>
                  </div>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed max-w-xs">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS ── */}
        <section className="py-16 sm:py-20 bg-[#F8F9FA]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-xs font-bold tracking-widest mb-4 whitespace-nowrap">REAL CUSTOMERS</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A1628]">What GTA Homeowners Say</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {TESTIMONIALS.map((t, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: t.stars }).map((_, si) => (
                      <div key={si} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-[#D4A853] text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed mb-5 italic">"{t.text}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                    <img src={t.img} alt={t.name} className="w-11 h-11 rounded-full object-cover object-top border-2 border-[#D4A853]/30" />
                    <div>
                      <p className="text-sm font-bold text-[#0A1628]">{t.name}</p>
                      <p className="text-xs text-gray-400">{t.city} · {t.service}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHY ALLIANCE ── */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-xs font-bold tracking-widest mb-4 whitespace-nowrap">WHY CHOOSE US</span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A1628] mb-6">The GTA's Most Trusted Security Company Since 1999</h2>
                <p className="text-gray-600 text-base leading-relaxed mb-8">
                  We're not a call centre — we're a local, family-owned business with certified technicians who live and work in the communities we protect. Every quote is prepared by a real expert, not an algorithm.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: 'ri-shield-star-line', title: 'Factory-Certified Technicians', desc: 'Certified by Honeywell, DSC, Hikvision, Qolsys & more.' },
                    { icon: 'ri-time-line', title: 'Same-Day Service Available', desc: 'Emergency support 24/7 across the entire GTA.' },
                    { icon: 'ri-money-dollar-circle-line', title: 'Transparent, Itemized Quotes', desc: 'No hidden fees. No pressure. Just honest pricing.' },
                    { icon: 'ri-customer-service-2-line', title: 'Lifetime Support Included', desc: 'We support your system for its entire lifespan.' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-11 h-11 bg-[#D4A853]/10 rounded-xl flex items-center justify-center shrink-0">
                        <i className={`${item.icon} text-xl text-[#D4A853]`}></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#0A1628] text-sm">{item.title}</p>
                        <p className="text-gray-500 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="w-full h-[420px] rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20security%20technician%20in%20uniform%20installing%20security%20camera%20on%20wall%20of%20modern%20home%20smiling%20confident%20expert%20installation%20residential%20property%20daylight%20clear%20sky&width=800&height=900&seq=gq-tech&orientation=portrait"
                    alt="Alliance Security technician"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                {/* Floating badge */}
                <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-4 border border-gray-100 flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#003DA5] rounded-xl flex items-center justify-center shrink-0">
                    <span className="text-white font-extrabold text-lg leading-none">A+</span>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[#0A1628]">BBB Accredited</p>
                    <p className="text-[10px] text-gray-500">A+ Rating Since 1999</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="py-16 sm:py-20 bg-[#F8F9FA]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-xs font-bold tracking-widest mb-4 whitespace-nowrap">FAQ</span>
              <h2 className="text-3xl font-extrabold text-[#0A1628]">Common Questions</h2>
            </div>
            <div className="space-y-3">
              {FAQS.map((faq, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-[#0A1628] text-sm pr-4">{faq.q}</span>
                    <div className="w-7 h-7 flex items-center justify-center shrink-0">
                      <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-[#D4A853] text-lg transition-transform`}></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5">
                      <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── BOTTOM CTA ── */}
        <section className="py-16 bg-[#0A1628] relative overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="w-16 h-16 bg-[#D4A853] rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-shield-check-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Ready to Protect Your Property?</h2>
            <p className="text-white/70 text-base mb-8 max-w-xl mx-auto">
              Join 30,000+ GTA homeowners and businesses who trust Alliance Security. Get your free quote today — no commitment required.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#get-quote-landing-form"
                onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="inline-flex items-center gap-2 bg-[#D4A853] text-white px-8 py-4 rounded-full font-bold text-base hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-xl"
              >
                <i className="ri-file-list-3-line text-lg"></i>
                Get My Free Quote
              </a>
              <a
                href="tel:18884589181"
                className="inline-flex items-center gap-2 border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold text-base hover:border-[#D4A853] hover:text-[#D4A853] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-lg"></i>
                1-888-458-9181
              </a>
            </div>
            <p className="text-white/40 text-xs mt-6">Mon–Fri 8am–8pm · Sat 9am–5pm · 24/7 Emergency Line</p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#050B14] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex flex-col leading-none">
            <span className="text-lg font-extrabold tracking-wider text-[#D4A853]">ALLIANCE</span>
            <span className="text-[9px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
          </Link>
          <div className="flex items-center gap-6 text-xs text-white/50">
            <Link to="/privacy-policy" className="hover:text-white/80 cursor-pointer">Privacy Policy</Link>
            <Link to="/contact" className="hover:text-white/80 cursor-pointer">Contact</Link>
            <Link to="/faq" className="hover:text-white/80 cursor-pointer">FAQ</Link>
            <Link to="/free-quote" className="hover:text-white/80 cursor-pointer">Detailed Quote</Link>
          </div>
          <p className="text-white/40 text-xs">© 2025 Alliance Security Systems. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function SuccessCard() {
  return (
    <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
      <div className="bg-emerald-500 px-7 py-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-check-double-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">Quote Request Sent!</h2>
            <p className="text-white/80 text-xs">We'll be in touch within 2 hours</p>
          </div>
        </div>
      </div>
      <div className="px-7 py-8 text-center">
        <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-5">
          <i className="ri-shield-check-line text-4xl text-emerald-500"></i>
        </div>
        <h3 className="text-xl font-bold text-[#0A1628] mb-3">Thank You!</h3>
        <p className="text-gray-500 text-sm leading-relaxed mb-6">
          A certified security specialist is reviewing your details and will contact you with a personalized quote within <strong>2 business hours</strong>.
        </p>
        <div className="bg-[#F8F9FA] rounded-2xl p-4 mb-6 text-left space-y-2">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">What happens next</p>
          {[
            'Specialist reviews your property details',
            'Custom quote prepared for your needs',
            'You receive a call or email within 2 hours',
          ].map((step, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
              <div className="w-5 h-5 bg-[#D4A853] rounded-full flex items-center justify-center shrink-0">
                <span className="text-white text-[10px] font-bold">{i + 1}</span>
              </div>
              {step}
            </div>
          ))}
        </div>
        <a
          href="tel:18884589181"
          className="flex items-center justify-center gap-2 bg-[#0A1628] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#D4A853] transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-phone-line"></i>
          Call Us: 1-888-458-9181
        </a>
      </div>
    </div>
  );
}
